import { useEffect, useMemo } from 'react';

import { useNextInfiniteRequest } from '@/renderer/utils/api/infiniteStore';

import { OddsCredit } from './OddsCredit';
import { PendingPicksTeaser } from './PendingPicksTeaser';
import { GhostMark, PlayList, SectionHead } from './PlayRow';
import { resultsListOpts } from './consts';

// Pending plays that belong in this section: everything ungraded EXCEPT futures (they'd
// bog it down and have their own tab). Sorted by game time so the next kickoff leads.
const NON_FUTURE_PLAY_TYPES = ['side', 'total', 'prop', 'prop-group'] as const;
const PENDING_LIMIT = 100;

/**
 * The "Pending" section: the current ungraded, non-future slate as custom play rows,
 * pinned above the graded Results so live/upcoming action leads (#2004). Works for both
 * the member (`me`, from their receipts) and the public/staff archive (`archive`, from
 * the release feed). For a signed-out or non-staff archive visitor the real picks are
 * paid, so this falls back to the blurred paywall teaser instead.
 */
export function PendingSection({
  userId,
  sportId,
  serviceIds,
  sportIdBase,
  isStaff = false,
  isAdmin = false,
  onCount,
}: {
  userId: API.Id | 'me' | 'archive';
  sportId?: number | number[];
  serviceIds?: number[];
  sportIdBase?: string;
  // Staff/admin. Archive: they see the real pending rows (everyone else gets the
  // paywall teaser). Both views: each pending row gets the Edit action.
  isStaff?: boolean;
  // Admin only: the Edit form may change the message's charge.
  isAdmin?: boolean;
  // Reports how many pending rows this view has, so the page can collapse empty modules.
  onCount?: (count: number) => void;
}) {
  const isArchive = userId === 'archive';
  const showTeaser = isArchive && !isStaff;

  const request = useNextInfiniteRequest<
    API.MessageReceipt | API.Release,
    void,
    (
      | API.WithPageInput<API.Release>
      | API.WithPageInput<API.ExtractNestedSortable<API.MessageReceipt>>
    ) & {
      withMarketLine?: boolean;
    }
  >(
    useMemo(
      () =>
        resultsListOpts(
          isArchive
            ? {
                key: 'fetchPendingReleases',
                req: '/api/v1/release',
                query: {
                  // Ascending, explicitly: the next kickoff leads, and the server
                  // adds `id` as the tiebreaker so a shared kickoff still lands in
                  // one fixed order (#2079).
                  order: { by: 'gameTime', dir: 'ASC' },
                  page: { limit: PENDING_LIMIT, offset: 0 },
                  withMarketLine: true,
                  data: {
                    // The release feed validates `result` as an array (the member's
                    // receipt feed accepts a scalar); a bare 'pending' 400s here.
                    result: ['pending'],
                    playType: [...NON_FUTURE_PLAY_TYPES],
                    [Array.isArray(sportId) ? 'sportId$in' : 'sportId']: sportId,
                    'message.serviceIds': serviceIds,
                  },
                },
                skipPoll: true,
                // Members and signed-out visitors don't read the release-feed pending here;
                // only the staff/admin archive view does (others get the teaser).
                pause: !isArchive || showTeaser,
              }
            : {
                key: 'fetchPendingReceipts',
                req: `/api/v1/messageReceipt/${userId}`,
                query: {
                  // Nested dot-path: a receipt reaches kickoff through its release.
                  // Ascending to match the archive branch above (and the ghost merge,
                  // which orders the combined set by kickoff ascending).
                  order: { by: 'release.gameTime', dir: 'ASC' },
                  page: { limit: PENDING_LIMIT, offset: 0 },
                  withMarketLine: true,
                  data: {
                    type: 'release',
                    result: 'pending',
                    playType: [...NON_FUTURE_PLAY_TYPES],
                    [Array.isArray(sportId) ? 'sportId$in' : 'sportId']: sportId,
                    'message.serviceIds': serviceIds,
                  },
                },
                skipPoll: true,
                pause: userId !== 'me',
              },
        ),
      [isArchive, showTeaser, userId, sportId, serviceIds],
    ),
  );

  const rows = showTeaser ? [] : (request.data?.data ?? []);
  // The list loads beside the page now, so a count of 0 before it lands is not an
  // answer. A paused list (nothing to load) is settled at 0.
  const paused = isArchive ? showTeaser : userId !== 'me';
  const settled = paused || !request.isPending;

  // Report upward so the page can tell "nothing here at all" from "nothing graded yet"
  // and collapse the modules that would otherwise render empty (#2004).
  useEffect(() => {
    if (settled) {
      onCount?.(rows.length);
    }
  }, [settled, rows.length, onCount]);

  // Non-staff archive: the paywall teaser stands in for the real (paid) pending slate.
  // The teaser carries its own per-sport headers and renders nothing when there are no
  // pending picks, so it drives its own section visibility (no dangling label).
  if (showTeaser) {
    return (
      <section className="mb-8 empty:hidden">
        <PendingPicksTeaser sportIdBase={sportIdBase} />
      </section>
    );
  }

  const ghosts = rows.filter(
    (row) => userId !== 'archive' && (row as API.MessageReceipt).ghost,
  ).length;
  // Ghosts are visible but aren't the member's, so the count is the plays that are.
  const ownedCount = rows.length - ghosts;

  // Nothing pending → no section at all. An empty state here just told you there was
  // nothing to see, and rendering it while a sport change was still in flight made the
  // section flash in and back out (#2004).
  if (rows.length === 0) {
    return null;
  }

  return (
    <section className="mb-8">
      <SectionHead label="Pending" count={ownedCount} aside={<OddsCredit currentLines />} />
      <PlayList
        rows={rows}
        groupByDate={false}
        canEdit={isStaff}
        canEditCharge={isAdmin}
        onGrade={request.refetch}
      />
      {ghosts > 0 && (
        <p className="mt-3 px-0.5 text-xs text-zinc-400 dark:text-zinc-500">
          Plays marked <GhostMark className="-mt-0.5 size-3.5 align-middle" /> were released before
          your subscription. They never count on your record and disappear once they grade.
        </p>
      )}
    </section>
  );
}
